// src/pages/departments/NutritionDietetics.js
import { motion } from "framer-motion";

const NutritionDietetics = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <section className="relative h-[300px] md:h-[400px] flex items-center justify-center">
        <img
          src="/images/department-hero.jpg"
          alt="Department Banner"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative text-center text-white px-6">
          <motion.h1
            className="text-3xl md:text-5xl font-extrabold mb-3"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            Department of Nutrition and Dietetics
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Promoting Health, Wellness, and Nutritional Excellence
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <div className="px-6 md:px-20 py-16">
        {/* History Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">History</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            The <strong>Department of Nutrition and Dietetics</strong> at 
            <strong> Federal Polytechnic Bida</strong> was established to address 
            the growing need for qualified nutritionists and dietitians capable 
            of combating malnutrition and promoting health through balanced diets 
            and sustainable nutrition practices.
          </p>
          <p className="text-gray-600 leading-relaxed mb-4">
            Since its inception, the department has been committed to training 
            competent professionals equipped with the scientific, technical, and 
            practical knowledge required to improve community health and wellness. 
            It operates under the guidelines of the 
            <strong> National Board for Technical Education (NBTE)</strong> and offers 
            accredited <strong>National Diploma (ND)</strong> programs, with plans 
            to expand into <strong>Higher National Diploma (HND)</strong> in the near future.
          </p>
          <p className="text-gray-600 leading-relaxed">
            The department blends classroom teaching with practical experience 
            through laboratory work, field training, and community nutrition outreach. 
            This approach ensures graduates are prepared for professional roles in 
            hospitals, schools, industries, and public health sectors.
          </p>
        </motion.section>

        {/* HOD Section */}
        <motion.section
          className="grid md:grid-cols-3 gap-8 items-center bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="md:col-span-1 flex justify-center">
            <img
              src="/images/hod-profile.jpg"
              alt="HOD"
              className="w-40 h-40 rounded-full object-cover shadow-lg border-4 border-blue-200"
            />
          </div>
          <div className="md:col-span-2">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Head of Department's Speech
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Dear students, colleagues, and stakeholders, welcome to the 
              <strong> Department of Nutrition and Dietetics</strong> at 
              <strong> Federal Polytechnic Bida</strong>. We are dedicated to 
              building a generation of nutrition professionals who are passionate 
              about improving health outcomes through science-based nutrition and 
              dietetic practices.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              The department focuses on equipping students with both theoretical 
              and practical knowledge of food, nutrition, and diet therapy. 
              Through innovative learning, research, and community engagement, 
              we strive to reduce malnutrition, promote dietary balance, and 
              foster public health sustainability.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Our mission is to empower you to be agents of change — advancing 
              nutrition awareness, wellness, and food security across Nigeria 
              and beyond. Together, let’s make healthy living a culture.
            </p>
            <p className="mt-4 text-blue-700 font-semibold">— Dr. Jane Doe</p>
            <p className="text-gray-500">Head of Department</p>
          </div>
        </motion.section>

        {/* Vision & Mission Section */}
        <motion.section
          className="grid md:grid-cols-2 gap-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Vision</h2>
            <p className="text-gray-600 leading-relaxed">
              To be a <strong>leading center of excellence</strong> in nutrition 
              education, research, and dietetic practice, producing skilled 
              graduates who contribute to national development and global health 
              improvement.
            </p>
          </div>
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Mission</h2>
            <p className="text-gray-600 leading-relaxed">
              To provide <strong>high-quality education and hands-on training</strong> 
              that equips students with scientific knowledge, professional ethics, 
              and practical skills for promoting nutritional well-being and disease 
              prevention in individuals and communities.
            </p>
          </div>
        </motion.section>

        {/* Why Study Nutrition & Dietetics */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Why Study Nutrition and Dietetics?
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Nutrition and Dietetics is a vital discipline that bridges food science, 
            human biology, and health. It plays an essential role in promoting 
            wellness, preventing diseases, and enhancing the quality of life through 
            appropriate dietary management and education.
          </p>
          <p className="text-gray-600 leading-relaxed mb-4">
            Studying this field provides an opportunity to make a real difference 
            in people’s lives — from creating healthy meal plans to addressing 
            complex health conditions such as obesity, diabetes, and malnutrition. 
            Students learn to apply nutritional knowledge in hospitals, schools, 
            public health programs, and food industries.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Graduates of this department are equipped to contribute to global 
            health goals, support sustainable food systems, and promote community 
            well-being through innovative dietetic interventions.
          </p>
        </motion.section>

        {/* Career Opportunities Section */}
        <motion.section
          className="bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Career Opportunities and Entry Requirements
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            Nutrition and Dietetics offers diverse career opportunities in the 
            healthcare, education, food, and wellness industries. Graduates can 
            serve in hospitals, public health agencies, and private practice, 
            or work as consultants in food service management and community nutrition.
          </p>
          <ul className="list-disc list-inside text-gray-700 mb-4">
            <li>Hospitals and Healthcare Institutions</li>
            <li>Community Nutrition and Public Health Programs</li>
            <li>Food and Beverage Industries</li>
            <li>Non-Governmental Organizations (NGOs)</li>
            <li>Educational Institutions and Research Centers</li>
            <li>Sports Nutrition and Fitness Consultancy</li>
            <li>Private Dietetic Practice and Wellness Coaching</li>
          </ul>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Entry Requirements</h3>
          <p className="text-gray-600 leading-relaxed">
            Applicants must possess <strong>five O’Level credit passes</strong> in 
            English Language, Mathematics, Biology, Chemistry, and Physics from 
            WAEC, NECO, or equivalent examinations, with a valid UTME score. 
            The <strong>ND program lasts two years (four semesters)</strong> and 
            emphasizes both academic and practical competence.
          </p>
        </motion.section>

        {/* Gallery Section */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Department Gallery
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["gallery1.jpg", "gallery2.jpg", "gallery3.jpg", "gallery4.jpg"].map(
              (img, i) => (
                <img
                  key={i}
                  src={`/images/${img}`}
                  alt={`Gallery ${i + 1}`}
                  className="w-full h-40 md:h-48 object-cover rounded-lg shadow-md hover:scale-105 transition-transform"
                />
              )
            )}
          </div>
        </motion.section>

        {/* Contact Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Contact the Department
          </h2>
          <p className="text-gray-600 mb-2">
            <strong>Address:</strong> Federal Polytechnic, Bida, Niger State, Nigeria
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Email:</strong> dept.nutrition@fedpolybida.edu.ng
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Phone:</strong> +234 800 000 0000
          </p>
        </motion.section>

        {/* Map Section */}
        <motion.section
          className="rounded-2xl overflow-hidden shadow-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
        >
          <iframe
            title="Department Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3926.278884327274!2d6.0125!3d9.0820!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104965e46b2e11f5%3A0xdeadbeef123456!2sFederal%20Polytechnic%20Bida!5e0!3m2!1sen!2sng!4v0000000000000"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </motion.section>
      </div>
    </div>
  );
};

export default NutritionDietetics;
